import { useEffect, useMemo, useRef, useState } from 'react';

import { GroupChannel, GroupChannelHandler } from '@sendbird/chat/groupChannel';
import {
  AdminMessage,
  BaseChannel,
  FileMessageCreateParams,
  GroupChannelListQueryParams,
  PreviousMessageListQuery,
  PreviousMessageListQueryParams,
  ReactionEvent,
} from '@sendbird/chat/lib/__definition';
import {
  BaseMessage,
  FileMessage,
  MessageTypeFilter,
  ThreadedMessageListParams,
  ThreadInfoUpdateEvent,
  UserMessage,
  UserMessageCreateParams,
} from '@sendbird/chat/message';
import { map } from 'lodash';
import { DateTime } from 'luxon';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  CHANNELS_LIMIT,
  MESSAGES_PER_PAGE,
} from '~/lib/constants/chat.constants';
import {
  ChatMessage,
  ChatUser,
  FileAttachment,
  UseChatProps,
  UseChatValues,
} from '~/lib/types/chat';
import ChatUtil, {
  getAdminMessageText,
  getAllowedChannels,
  getNotifyUserIds,
  getUserFromAdminMessage,
  isActiveTreatmentStatus,
  isExternalChannel,
  isInactiveRotatedChannel,
  isReadOnlyChannel,
  markMessageAsRemoved,
  removeUUIDFromFilename,
} from '~/lib/util/chat/chat.utils';
import { renderLinks, renderNewLine } from '~/lib/util/chat/chatSearch.util';

import useMessageStatusCache from './useMessageStatusCache';
import useRemovedMessageCache from './useRemovedMessageCache';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { CHAT_HANDLERS_EQUIP_CHANNEL_KEY } from '~/lib/common/SendbirdChatWrapper';
import { useSendbirdReadyContext } from '~/lib/contexts/SendbirdReadyContext';
import {
  useLazyGetGroupChannelMessagesQuery,
  useLazyGetGroupChannelsByPatientIdQuery,
  useRotateChannelMutation,
} from '~/store/services/chat';
import { useDatadog } from '~/lib/hooks/useDatadog';

const loadPreviousMessagesParams: PreviousMessageListQueryParams = {
  includeParentMessageInfo: true,
  includeReactions: true,
  includeThreadInfo: true,
  limit: MESSAGES_PER_PAGE,
  reverse: true,
};

const useChat = ({
  channel: selectedChannel,
  onChannelUpdated,
  chatUserId,
  patientId,
  universalStatusCode,
}: UseChatProps): UseChatValues => {
  const { currentUser } = useGlobalContext();
  const { sdk: sendBirdSdkInstance } = useSendbirdReadyContext();
  const { logError } = useDatadog();
  const isPatientInTreatment = isActiveTreatmentStatus(universalStatusCode);

  const [channels, updateChannels] = useState<GroupChannel[]>([]);
  const [chatMessages, updateChatMessages] = useState<ChatMessage[]>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [isChannelsLoading, setIsChannelsLoading] = useState<boolean>(true);
  const [isChannelsError, setIsChannelsError] = useState<boolean>(false);
  const messageListRef = useRef<ChatMessage[]>(null);
  const shouldUpdateChannelRef = useRef(true);
  const { isInCache } = useRemovedMessageCache();
  const [getGroupChannelsByPatientId] =
    useLazyGetGroupChannelsByPatientIdQuery();
  const [getGroupChannelMessages] = useLazyGetGroupChannelMessagesQuery();
  const [rotateChannel] = useRotateChannelMutation();

  const [newReplyReceived, setNewReplyReceived] = useState<ChatMessage>(null);
  const { maudProviderChatMessagesPrototype: isMessageThreadingEnabled } =
    useFlags<LDFlags>();

  const {
    addToCache,
    isInCache: isMessageStatusInCache,
    removeBulkFromCache,
  } = useMessageStatusCache();

  messageListRef.current = chatMessages;

  const updateChannelList = (updatedChannel: GroupChannel) => {
    updateChannels((prev) =>
      (prev ?? []).map((channel) => {
        if (channel.url === updatedChannel.url) return updatedChannel;

        return channel;
      }),
    );
  };

  const updateMessageList = (
    messageIndex: number,
    updatedMessage: ChatMessage,
  ) => {
    const newMessages = [...(chatMessages ?? [])];
    newMessages.splice(messageIndex, 1, updatedMessage);
    updateChatMessages(newMessages);
  };

  const mapChannels = (
    channels: any[],
    isRotatedChannel: boolean,
  ): GroupChannel[] => {
    return channels.map((c) => ({
      ...c,
      createPreviousMessageListQuery: (): PreviousMessageListQuery =>
        ({
          hasNext: false,
          isLoading: false,
        }) as PreviousMessageListQuery,
      isFileMessage: () =>
        c.last_message?.file && c.last_message?.type === MessageTypeFilter.FILE,
      isReadMessage: () => {},
      isFrozen: c.freeze,
      isRotatedChannel,
      members: c.members.map((m) => ({
        ...m,
        metaData: m.metadata,
        userId: m.user_id,
      })),
      url: c.channel_url,
    }));
  };

  const loadChannels = async () => {
    if (sendBirdSdkInstance?.groupChannel) {
      setIsChannelsError(false);
      try {
        const params: GroupChannelListQueryParams = {
          includeEmpty: true,
          limit: CHANNELS_LIMIT,
          metadataKey: 'patientId',
          metadataValues: [patientId],
        };

        const myGroupChannelListQuery =
          sendBirdSdkInstance.groupChannel.createMyGroupChannelListQuery(
            params,
          );

        if (myGroupChannelListQuery.hasNext) {
          const [myGroupChannels, rotatedChannels] = await Promise.all([
            myGroupChannelListQuery
              .next()
              .then((foundChannels: GroupChannel[]) => {
                return getAllowedChannels(foundChannels, isPatientInTreatment);
              })
              .catch((e) => {
                console.error(
                  'Could not loadChannels, myGroupChannelListQuery.next(): ',
                  e,
                );
                logError(e);
                setIsChannelsError(true);
                return [];
              }),
            getGroupChannelsByPatientId({ patientId })
              .unwrap()
              .then((res) => {
                const foundChannels: GroupChannel[] = mapChannels(
                  res.channels,
                  true,
                );
                return getAllowedChannels(foundChannels, isPatientInTreatment);
              })
              .catch((e) => {
                console.error(
                  'Could not loadChannels, getGroupChannelsByPatientId: ',
                  e,
                );
                logError(e);
                setIsChannelsError(true);
                return [];
              }),
          ]);

          const isCurrentUserMember = (c: GroupChannel) =>
            c.members.some((m) => currentUser.userId === m.userId);
          const hasChannelUrlMatch = (c: GroupChannel) =>
            myGroupChannels?.some((fc) => fc.url === c.url);
          const missingRotatedChannels = rotatedChannels?.filter(
            (rc) => !hasChannelUrlMatch(rc) && isCurrentUserMember(rc),
          );
          const allMyChannels = [...myGroupChannels, ...missingRotatedChannels];

          updateChannels(allMyChannels);
        }
      } catch (e) {
        console.error('Could not loadChannels', e);
        logError(e);
        setIsChannelsError(true);
      } finally {
        setIsChannelsLoading(false);
      }
    } else {
      console.error('Sendbird is not initialized');
    }
  };

  const processNonAdminMessage = (
    newMessage: BaseMessage,
    isRead?: boolean,
  ): ChatMessage => {
    const isFileMessage = newMessage.isFileMessage();
    const {
      channelUrl,
      createdAt,
      data,
      mentionedUsers,
      messageId,
      parentMessageId,
      sender,
      threadInfo,
    } = isFileMessage
      ? (newMessage as FileMessage)
      : (newMessage as UserMessage);

    const message = isFileMessage
      ? removeUUIDFromFilename((newMessage as FileMessage).name)
      : (newMessage as UserMessage).message;

    const calculatedMentionedUserIds = (mentionedUsers ?? [])
      .map((x) => x.userId)
      .concat(sender.userId);

    return {
      allMentionedUserIds: ChatUtil.excludeCurrentUserId(
        calculatedMentionedUserIds,
        currentUser.userId,
      ),
      channelUrl,
      createdAt,
      data,
      hasNewReply: isMessageStatusInCache(messageId),
      hasThread: !!threadInfo,
      id: messageId?.toFixed(),
      isAdminMessage: false,
      isFileMessage,
      isMentioned: calculatedMentionedUserIds.includes(chatUserId),
      isMyMessage: currentUser.userId === sender?.userId,
      isNewThread: false,
      isRead:
        isRead ||
        selectedChannel?.isReadMessage(newMessage) ||
        sender.userId === chatUserId,
      isRemoved: isInCache(messageId),
      mentionedUserIds: (mentionedUsers ?? []).map((user) => user.userId),
      mentionedUsers: mentionedUsers ?? [],
      message,
      messageId,
      parentMessageId,
      relativeDate: ChatUtil.calculateRelativeDate(createdAt),
      sbMessage: newMessage,
      sender,
      thread: [],
      threadInfo,
      timestamp: DateTime.fromMillis(createdAt).toFormat('hh:mm a'),
    };
  };

  const processAdminMessage = (
    newMessage: BaseMessage,
    isRead?: boolean,
  ): ChatMessage => {
    const {
      channelUrl,
      createdAt,
      data,
      mentionedUserIds,
      mentionedUsers,
      message,
      messageId,
      parentMessageId,
      threadInfo,
    } = newMessage as AdminMessage;

    const sender = getUserFromAdminMessage(message);

    return {
      allMentionedUserIds: mentionedUserIds,
      channelUrl,
      createdAt,
      data,
      hasNewReply: false,
      hasThread: !!threadInfo,
      id: messageId?.toFixed(),
      isAdminMessage: true,
      isFileMessage: false,
      isMentioned: false,
      isMyMessage: false,
      isNewThread: false,
      isRead: isRead || selectedChannel?.isReadMessage(newMessage),
      isRemoved: isInCache(messageId),
      mentionedUserIds,
      mentionedUsers,
      message: getAdminMessageText(message, sender?.nickname),
      messageId,
      parentMessageId,
      relativeDate: ChatUtil.calculateRelativeDate(createdAt),
      sbMessage: newMessage,
      sender,
      thread: [],
      threadInfo,
      timestamp: DateTime.fromMillis(createdAt).toFormat('hh:mm a'),
    };
  };

  const processMessages = (newMessages: BaseMessage[]): ChatMessage[] => {
    const processedMessages: ChatMessage[] = [];
    const isChannelReadOnly = isReadOnlyChannel(
      selectedChannel,
      currentUser.userId,
    );

    for (const newMessage of newMessages) {
      if (!newMessage.isAdminMessage()) {
        processedMessages.push(
          processNonAdminMessage(newMessage, isChannelReadOnly),
        );
      } else {
        processedMessages.push(
          processAdminMessage(newMessage, isChannelReadOnly),
        );
      }
    }

    return renderLinks(renderNewLine(processedMessages));
  };

  const messageQuery = useMemo<PreviousMessageListQuery>(
    () =>
      selectedChannel?.createPreviousMessageListQuery(
        loadPreviousMessagesParams,
      ),
    [selectedChannel?.url],
  );

  const mapMessages = (
    messages: any[],
    parentMessageId: number,
  ): BaseMessage[] =>
    messages?.map((m) => ({
      ...m,
      createdAt: m.created_at,
      file: m.file,
      files: m.files,
      isAdminMessage: () => m.type === 'ADMM',
      isFileMessage: () => m.type === MessageTypeFilter.FILE,
      messageId: m.message_id,
      name: m.file?.name, // required for file names?
      parentMessageId,
      plainUrl: m.type === MessageTypeFilter.FILE && m.file.url,
      sender: { ...m.user, userId: m.user?.user_id },
      threadInfo: m.thread_info,
    }));

  const loadMessages = (
    onSuccess?: (messages: ChatMessage[], hasMoreMessages: boolean) => void,
  ) => {
    if (!isInactiveRotatedChannel(selectedChannel)) {
      if (messageQuery?.hasNext) {
        messageQuery
          .load()
          .then((messages) => {
            const processedMessages = processMessages(
              messages.filter((message) => message.parentMessageId === 0),
            );

            updateChatMessages([
              ...(messageListRef?.current || []),
              ...processedMessages,
            ]);
            onSuccess?.(processMessages(messages), messageQuery?.hasNext);
          })
          .catch((error) => logError(error))
          .finally(() => {
            setHasMoreMessages(messageQuery?.hasNext);
          });
      }
    } else {
      getGroupChannelMessages({
        channel_url: selectedChannel.url,
        message_ts: Date.now(), //oldestMessage ? oldestMessage.createdAt : Date.now(),
        next_limit: 0,
        prev_limit: MESSAGES_PER_PAGE,
      })
        .unwrap()
        .then((res) => {
          const messages = mapMessages(res?.messages, 0);
          const filteredMessages = messages.filter(
            (message) => message.parentMessageId === 0,
          );
          const processedMessages = processMessages(filteredMessages);

          // Use functional state update to avoid stale closures
          updateChatMessages((prevMessages) => {
            if (!prevMessages) {
              return [...processedMessages];
            } else {
              return [...prevMessages, ...processedMessages];
            }
          });

          // Determine if there are more messages to load
          const hasMore = res.messages.length === MESSAGES_PER_PAGE;
          setHasMoreMessages(hasMore);

          onSuccess?.(processedMessages, hasMore);
        })
        .catch((e) => {
          console.error(
            'Failed to get messages for (rotated) channel: ',
            selectedChannel?.url,
            e,
          );
          logError(e);
        });
    }
  };

  const addToMessageThread = (
    messageId: number,
    messages: BaseMessage[],
  ): ChatMessage => {
    const existingParentMessageIndex = ChatUtil.findMessageIndex(
      messageListRef?.current ?? [],
      messageId,
    );

    const existingParentMessage =
      messageListRef?.current?.[existingParentMessageIndex] ??
      ({} as ChatMessage);

    const replies = processMessages(messages);

    existingParentMessage.hasThread = true;
    existingParentMessage.thread = ChatUtil.mergeMessages(
      existingParentMessage.thread,
      replies,
    );
    existingParentMessage.hasNewReply = replies.some((reply) => !reply.isRead);
    existingParentMessage.allMentionedUserIds = ChatUtil.getAllMentionedUserIds(
      replies,
    ).concat(existingParentMessage.mentionedUserIds);

    updateMessageList(existingParentMessageIndex, existingParentMessage);

    return existingParentMessage;
  };

  const getReplies = (
    message: ChatMessage,
    onSuccess?: (replies: ChatMessage[]) => void,
  ) => {
    const params: ThreadedMessageListParams = {
      includeParentMessageInfo: true,
      includeReactions: true,
      isInclusive: true,
      nextResultSize: MESSAGES_PER_PAGE,
      prevResultSize: 0,
    };

    (message.isFileMessage
      ? (message.sbMessage as FileMessage)
      : (message.sbMessage as UserMessage)
    )
      .getThreadedMessagesByTimestamp(message.createdAt, params)
      .then((value) => {
        const { parentMessage, threadedMessages } = value;
        if (parentMessage.messageId === message.messageId) {
          const existingParentMessage = addToMessageThread(
            parentMessage.messageId,
            threadedMessages,
          );
          onSuccess?.(existingParentMessage.thread);
        }
      });
  };

  const retrieveChannel = (
    channelUrl: string,
    onSuccess: (retrievedChannel: GroupChannel) => void,
    onFailure: () => void,
  ) => {
    if (sendBirdSdkInstance) {
      sendBirdSdkInstance.groupChannel.getChannel(channelUrl).then(onSuccess);
    } else {
      onFailure();
    }
  };

  const getAllParentsAndThreads = (): ChatMessage[] =>
    messageListRef?.current?.flatMap((m) => [m, ...(m.thread ?? [])]);

  const findMessageById = (messageId: number): ChatMessage => {
    const allMessages: ChatMessage[] = getAllParentsAndThreads();
    return allMessages.find((t) => t.messageId === messageId);
  };

  const handleMarkChannelThreadsAsRead = () => {
    const curMessageRefs = messageListRef.current ?? [];
    const markThreadPromises = [];
    curMessageRefs.forEach((m) => {
      if (m?.sbMessage?.threadInfo?.unreadReplyCount > 0)
        markThreadPromises.push(m.sbMessage.markThreadAsRead());
    });
    if (markThreadPromises.length > 0) {
      Promise.all(markThreadPromises).catch((error) => {
        console.error(
          'Failed to mark one or more message threads as read:',
          error,
        );
      });
    }
  };

  const markChannelMessagesAsRead = (
    currentChannel: GroupChannel,
    onSuccess?: () => void,
    onFailure?: () => void,
  ) => {
    currentChannel
      .markAsRead()
      .then(() => {
        const updatedMessages = (messageListRef.current ?? []).map(
          (message) => {
            const replies = (message.thread ?? []).map((reply) => ({
              ...reply,
              isRead: true,
            }));
            return {
              ...message,
              hasNewReply: false,
              isRead: true,
              thread: replies,
            };
          },
        );
        if (isMessageThreadingEnabled) {
          handleMarkChannelThreadsAsRead();
        }
        updateChatMessages(updatedMessages);
        updateChannelList(currentChannel);
        removeBulkFromCache(map(messageListRef.current ?? [], 'messageId'));

        onSuccess?.();
      })
      .catch((error) => {
        console.error('Failed to mark channel messages as read:', error);
        logError(error);
        onFailure?.();
      });
  };

  const markMessagesAsRead = (
    channelUrl: string,
    onSuccess?: () => void,
    onFailure?: () => void,
  ) => {
    retrieveChannel(
      channelUrl,
      (retrievedChannel) =>
        markChannelMessagesAsRead(retrievedChannel, onSuccess, onFailure),
      () => {
        onFailure?.();
      },
    );
  };

  const sendFileMessage = (
    attachment: FileAttachment,
    fileS3Url: string,
    parentMessageId: number,
    mentionedUserIds: string[],
    onSuccess?: () => void,
  ) => {
    const parentMessage = (chatMessages ?? []).find(
      (message) => message.messageId === parentMessageId,
    );

    const sendFileMessageParams: FileMessageCreateParams = {
      data: parentMessageId
        ? JSON.stringify({
            notifyUserIds: getNotifyUserIds(
              parentMessage,
              currentUser.userId,
              mentionedUserIds,
              [
                parentMessage?.sender.userId,
                ...(parentMessage?.allMentionedUserIds ?? []),
              ],
              selectedChannel?.members as ChatUser[],
            ),
            parentMessageId,
          })
        : '',
      fileName: attachment.name,
      fileSize: attachment.file.size,
      fileUrl: fileS3Url,
      mentionedUserIds,
      mimeType: attachment.file.type,
      parentMessageId,
    };

    selectedChannel
      ?.sendFileMessage(sendFileMessageParams)
      .onSucceeded((sendableMessage) => {
        if (parentMessageId > 0) {
          addToMessageThread(parentMessageId, [sendableMessage]);
        } else {
          const processedMessages = processMessages([sendableMessage]);

          updateChatMessages([
            ...processedMessages,
            ...(messageListRef.current ?? []),
          ]);
        }
        onSuccess?.();
      })
      .onFailed((error) => console.log(error));
  };

  const sendUserMessage = (
    messageToSend: ChatMessage,
    onSuccess?: () => void,
  ) => {
    const sendMessageParams: UserMessageCreateParams = {
      data: messageToSend.data,
      mentionedUserIds: messageToSend.mentionedUserIds,
      mentionedUsers: messageToSend.mentionedUsers,
      message: messageToSend.message,
    };

    selectedChannel
      ?.sendUserMessage(sendMessageParams)
      .onSucceeded((sendableMessage) => {
        const processedMessages = processMessages([sendableMessage]);
        updateChatMessages([
          ...processedMessages,
          ...(messageListRef?.current ?? []),
        ]);

        onSuccess?.();
      });
  };

  const sendMessageReply = (
    messageToSend: ChatMessage,
    onSuccess?: (sentMessages: ChatMessage) => void,
  ) => {
    const { mentionedUserIds, mentionedUsers, message, parentMessageId } =
      messageToSend;

    const parentMessage = (chatMessages ?? []).find(
      (message) => message.messageId === parentMessageId,
    );

    const sendMessageParams: UserMessageCreateParams = {
      data: JSON.stringify({
        notifyUserIds: getNotifyUserIds(
          messageToSend,
          currentUser.userId,
          mentionedUserIds,
          [
            parentMessage?.sender.userId,
            ...(parentMessage?.allMentionedUserIds ?? []),
          ],
          selectedChannel?.members as ChatUser[],
        ),
        parentMessageId,
      }),
      mentionedUserIds,
      mentionedUsers,
      message,
      parentMessageId,
    };

    selectedChannel
      ?.sendUserMessage(sendMessageParams)
      .onSucceeded((sendableMessage) => {
        const sentMessage = processMessages([sendableMessage])[0];

        const parentMessageIndex = ChatUtil.findMessageIndex(
          messageListRef?.current,
          parentMessageId,
        );

        if (parentMessageIndex > -1) {
          const parentMessage =
            messageListRef?.current?.[parentMessageIndex] ??
            ({} as ChatMessage);

          parentMessage.hasThread = true;
          parentMessage.thread.push(sentMessage);
          parentMessage.isNewThread = parentMessage.thread.length === 1;
          updateMessageList(parentMessageIndex, parentMessage);

          onSuccess?.(sentMessage);
        }
      });
  };

  const removeMessage = async (messageId: number, replyId?: number) => {
    const updatedMessage = await markMessageAsRemoved(
      selectedChannel,
      chatMessages,
      messageId,
      replyId,
    );

    if (updatedMessage) {
      updateMessageList(updatedMessage.messageIndex, updatedMessage.message);
    }
  };

  useEffect(() => {
    // GX-2531 - channel rotation - joseph
    //  TODO: Move any selectedChannel logic to a custom useChatChannel hook to avoid circular dependencies
    const rotateChannelAndLoadMessages = async () => {
      await rotateChannel({ channelUrl: selectedChannel.url })
        .unwrap()
        .catch((e) => console.error('failed to rotate channel', e));
      await sendBirdSdkInstance.groupChannel
        .getChannel(selectedChannel.url)
        .then((rotatedChannel) => {
          updateChannelList(rotatedChannel);
          onChannelUpdated?.(rotatedChannel);
        })
        .catch((e) => {
          console.error(
            'failed to rotate channel - falling back to original',
            e,
          );
        })
        .finally(() => loadMessages());
    };

    // need to reset messages when switching channels
    updateChatMessages(null);

    if (selectedChannel) {
      if (isInactiveRotatedChannel(selectedChannel)) {
        void rotateChannelAndLoadMessages();
      } else {
        loadMessages();
      }
    }
  }, [selectedChannel]);

  const getChannelHandler = (): GroupChannelHandler => {
    const channelHandler = new GroupChannelHandler();

    channelHandler.onThreadInfoUpdated = (
      channel: GroupChannel,
      threadInfoUpdateEvent: ThreadInfoUpdateEvent,
    ) => {
      shouldUpdateChannelRef.current = false;
      const currentChannelUrl =
        messageListRef?.current?.length > 0
          ? messageListRef.current[0].channelUrl
          : channel.url;
      if (
        channel.url === currentChannelUrl &&
        threadInfoUpdateEvent.targetMessageId > 0
      ) {
        const updatedMessageIndex = ChatUtil.findMessageIndex(
          messageListRef?.current ?? [],
          threadInfoUpdateEvent.targetMessageId,
        );

        if (updatedMessageIndex > -1) {
          const updatedMessage = ChatUtil.updateMessage(
            messageListRef?.current?.[updatedMessageIndex],
            {
              isAdminMessage: false, // We are not allowing threads in admin messages. Since, there is a thread message update event fired essentially means that the message is a non-admin message
              isMyMessage:
                messageListRef?.current?.[updatedMessageIndex].sender.userId ===
                currentUser.userId,
              threadInfo: threadInfoUpdateEvent.threadInfo,
            },
          );
          const lastMessageInfo = updatedMessage.thread?.slice(-1)[0];
          if (lastMessageInfo) {
            const {
              channelUrl,
              createdAt,
              data,
              isFileMessage,
              mentionedUserIds,
              mentionedUsers,
              message,
              messageId,
              parentMessageId,
              threadInfo,
            } = lastMessageInfo;
            updateChannelList({
              ...channel,
              lastMessage: {
                ...channel.lastMessage,
                channelUrl,
                createdAt,
                data,
                isFileMessage: () => isFileMessage,
                mentionedUserIds,
                mentionedUsers,
                message,
                messageId,
                name: message,
                parentMessageId,
                plainUrl: '',
                threadInfo,
              },
            } as unknown as GroupChannel);
          }
          if (
            updatedMessage.thread.length !==
            updatedMessage.threadInfo.replyCount
          ) {
            getReplies(updatedMessage, () => {
              updateMessageList(updatedMessageIndex, updatedMessage);
            });
          } else {
            // TODO: figure this out, this isn't working right and was causing console errors
            // updateMessageList(updatedMessageIndex, updatedMessage);
          }
        }
      }
    };

    channelHandler.onChannelChanged = (updatedChannel) => {
      shouldUpdateChannelRef.current = true;
      setTimeout(() => {
        if (shouldUpdateChannelRef.current)
          updateChannelList(updatedChannel as GroupChannel);
      }, 500);
    };

    channelHandler.onMessageReceived = (
      updatedChannel: BaseChannel,
      message: BaseMessage,
    ) => {
      const receivedMessage = processMessages([message])[0];
      const isCurrentUserSender =
        receivedMessage.sender.userId === currentUser.userId;

      if (!isCurrentUserSender) {
        updateChannelList(updatedChannel as GroupChannel);
      }

      const currentChannelUrl =
        messageListRef?.current?.length > 0
          ? messageListRef.current?.[0]?.channelUrl
          : selectedChannel?.url;
      // Ignore messages received for channels other than currently selected channel
      if (message.channelUrl === currentChannelUrl) {
        if (message.parentMessageId > 0) {
          const parentMessageIndex = ChatUtil.findMessageIndex(
            messageListRef?.current,
            message.parentMessageId,
          );

          if (parentMessageIndex > -1) {
            const parentMessage = messageListRef?.current?.[parentMessageIndex];

            setNewReplyReceived(receivedMessage);
            parentMessage.hasNewReply = true;
            parentMessage.hasThread = true;
            parentMessage.thread.push(receivedMessage);
            updateMessageList(parentMessageIndex, parentMessage);
          }
        } else {
          if (!isCurrentUserSender) {
            updateChatMessages([
              receivedMessage,
              ...(messageListRef?.current ?? []),
            ]);
          }
        }
      } else if (message.parentMessageId > 0) {
        addToCache(message.parentMessageId);
      }
    };

    channelHandler.onMessageUpdated = (
      channel: BaseChannel,
      message: BaseMessage,
    ) => {
      const messageIndex = messageListRef?.current?.findIndex(
        (x) => x.messageId === message.messageId,
      );

      if (messageIndex > -1) {
        const processedMessage = processMessages([message])[0];

        messageListRef?.current?.splice(messageIndex, 1, processedMessage);
        updateChatMessages([...(messageListRef?.current ?? [])]);
      }
    };

    channelHandler.onReactionUpdated = (
      channel: BaseChannel,
      reactionEvent: ReactionEvent,
    ) => {
      const foundMessage: ChatMessage = findMessageById(
        reactionEvent.messageId,
      );
      if (foundMessage)
        foundMessage.sbMessage.applyReactionEvent(reactionEvent);
      else
        console.error(
          'could not find message for reactionEvent: ',
          reactionEvent,
        );
    };

    return channelHandler;
  };

  useEffect(() => {
    const groupChannelModule = sendBirdSdkInstance?.groupChannel;
    groupChannelModule?.addGroupChannelHandler(
      CHAT_HANDLERS_EQUIP_CHANNEL_KEY,
      getChannelHandler(),
    );

    // Every useChat owner registers under one shared key, so the last to
    // register wins. Releasing the key on unmount keeps the handler bound to
    // the owner that is still mounted.
    return () =>
      groupChannelModule?.removeGroupChannelHandler(
        CHAT_HANDLERS_EQUIP_CHANNEL_KEY,
      );
  }, [sendBirdSdkInstance]);

  return {
    channels,
    chatMessages,
    getReplies,
    hasMoreMessages,
    isChannelReadOnly: useMemo(
      () => isReadOnlyChannel(selectedChannel, currentUser.userId),
      [selectedChannel, currentUser.userId],
    ),
    isChannelsError,
    isChannelsLoading,
    isGroupChannel: useMemo(
      () => !isExternalChannel(selectedChannel?.url),
      [selectedChannel],
    ),
    loadChannels,
    loadMessages,
    markMessagesAsRead,
    newReplyReceived,
    removeMessage,
    sendFileMessage,
    sendMessageReply,
    sendUserMessage,
  };
};

export default useChat;
